import { addXAuthTokenHeaders } from '@common/apis/customAddHeaders';
import customStorageAdapter from '@common/apis/customStorageAdpter';
import { createAlova } from 'alova';
import GlobalFetch from 'alova/GlobalFetch';
import ReactHook from 'alova/react';

const forceLogoutOnDeletedCustomer = async () => {
  window.dispatchEvent(
    new CustomEvent('logout_trail', {
      detail: {
        trigger: 'identity_customer_not_found_ac_interceptor',
        source: 'ac_alova_interceptor',
        pathname: window.location.pathname,
      },
    }),
  );

  const { clearACLocalData } = await import('@libs/acp/utils/handleRefreshToken');
  await clearACLocalData();
  window.location.href = '/login';
};

interface acInterceptorProps {
  baseURL: string;
  headersBeforeRequest?: object;
  addXAuthToken?: boolean;
}

/**
 * Creates an instance of Alova with the provided configurations.
 * @property {string} baseURL The base URL for the requests of the Alova instance.
 * @property {Object} [headersBeforeRequest] - Custom headers to be included in requests.
 * @property {boolean} [addXAuthToken = true] - Indicates whether the X-Auth-Token header should be added to the requests. Default is true.
 * @returns {AlovaInstance} The created Alova instance.
 */
const acInterceptor = ({
  baseURL,
  headersBeforeRequest,
  addXAuthToken = true,
}: acInterceptorProps) =>
  createAlova({
    statesHook: ReactHook,
    requestAdapter: GlobalFetch(),
    baseURL,
    storageAdapter: customStorageAdapter,
    responded: {
      onSuccess: async (response) => {
        const details = {
          bodyUsed: response.bodyUsed,
          status: response.status,
          statusText: response.statusText,
          type: response.type,
          url: response.url,
        };

        const json = await response.json().catch(() => ({}));

        if (response.status >= 400) {
          const errorContext = { ...json, ...details };
          if (json?.error_slug === 'customer_not_found') await forceLogoutOnDeletedCustomer();
          throw errorContext;
        };

        return json;
      },

      onError: (error) => {
        window?.DD_RUM?.onReady(() => {
          window?.DD_RUM?.addError(error);
        });
        
        throw error
      },
    },
    async beforeRequest(method) {
      if (baseURL?.includes('geminio')) return;

      method.config.headers = { ...headersBeforeRequest };
      if (addXAuthToken) await addXAuthTokenHeaders(method);
    },
  });

export default acInterceptor;
